import React, { useState, useCallback } from 'react';
import { Copy, Trash2, CheckCircle2, AlertTriangle, Loader2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${window.__WR_API_BASE__}/api`;

/**
 * HistoryDedup — UI for detecting and merging duplicate history entries.
 * Shows duplicate groups with similarity scores and merge actions.
 */
const HistoryDedup = ({ onMergeComplete }) => {
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState(null);
  const [totalDuplicates, setTotalDuplicates] = useState(0);
  const [merging, setMerging] = useState(null);

  const scanForDuplicates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/history/duplicates`, { withCredentials: true });
      setGroups(response.data.groups);
      setTotalDuplicates(response.data.total_duplicates);
      if (response.data.groups.length === 0) {
        toast.success('No duplicates found!');
      }
    } catch (error) {
      toast.error('Failed to scan for duplicates');
    } finally {
      setLoading(false);
    }
  }, []);

  const mergeGroup = useCallback(async (group) => {
    const keepEntry = group.entries[0]; // Keep the newest (first since sorted desc)
    const removeIds = group.entries.slice(1).map(e => e.id);

    setMerging(keepEntry.id);
    try {
      const response = await axios.post(`${API}/history/merge`, {
        keep_id: keepEntry.id,
        remove_ids: removeIds,
      }, { withCredentials: true });
      toast.success(`Merged ${response.data.merged} duplicate(s)`);
      // Remove the merged group from state
      setGroups(prev => prev.filter(g => g.entries[0].id !== keepEntry.id));
      setTotalDuplicates(prev => prev - removeIds.length);
      if (onMergeComplete) onMergeComplete();
    } catch (error) {
      toast.error('Failed to merge entries');
    } finally {
      setMerging(null);
    }
  }, [onMergeComplete]);

  const mergeAll = useCallback(async () => {
    if (!groups || groups.length === 0) return;
    setMerging('all');
    let totalMerged = 0;
    for (const group of groups) {
      const keepEntry = group.entries[0];
      const removeIds = group.entries.slice(1).map(e => e.id);
      try {
        const response = await axios.post(`${API}/history/merge`, {
          keep_id: keepEntry.id,
          remove_ids: removeIds,
        }, { withCredentials: true });
        totalMerged += response.data.merged;
      } catch (error) {
        console.error('Merge error:', error);
      }
    }
    toast.success(`Merged ${totalMerged} duplicate(s) total`);
    setGroups([]);
    setTotalDuplicates(0);
    setMerging(null);
    if (onMergeComplete) onMergeComplete();
  }, [groups, onMergeComplete]);

  const truncate = (text, len = 80) => text?.substring(0, len) + (text?.length > len ? '...' : '') || '';
  const formatTs = (ts) => {
    try { return new Date(ts).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return ts; }
  };

  return (
    <div className="space-y-3" data-testid="history-dedup">
      {/* Scan Button */}
      {groups === null && (
        <Button
          onClick={scanForDuplicates}
          disabled={loading}
          variant="outline"
          size="sm"
          className="w-full gap-2"
          data-testid="scan-duplicates-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}
          {loading ? 'Scanning...' : 'Scan for Duplicates'}
        </Button>
      )}

      {/* Results */}
      {groups !== null && groups.length === 0 && (
        <div className="text-center p-4 text-muted-foreground" data-testid="no-duplicates">
          <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-green-500" />
          <p className="text-sm font-medium">No duplicates found</p>
          <Button variant="ghost" size="sm" onClick={() => setGroups(null)} className="mt-2 text-xs">
            Scan Again
          </Button>
        </div>
      )}

      {groups !== null && groups.length > 0 && (
        <>
          {/* Summary */}
          <div className="flex items-center justify-between p-2 bg-amber-500/10 border border-amber-500/20 rounded-lg">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              <span className="text-sm font-medium">{totalDuplicates} duplicate{totalDuplicates !== 1 ? 's' : ''} in {groups.length} group{groups.length !== 1 ? 's' : ''}</span>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={mergeAll}
                disabled={!!merging}
                className="h-7 text-xs gap-1"
                data-testid="merge-all-btn"
              >
                {merging === 'all' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                Merge All
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setGroups(null)} className="h-7 text-xs">
                <X className="w-3 h-3" />
              </Button>
            </div>
          </div>

          {/* Duplicate Groups */}
          {groups.map((group, gi) => (
            <Card key={gi} className="border-border bg-card/50" data-testid={`dedup-group-${gi}`}>
              <CardHeader className="p-3 pb-2">
                <CardTitle className="text-xs font-medium text-muted-foreground flex items-center justify-between">
                  <span>{group.count} entries &middot; {group.similarity}% similar</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => mergeGroup(group)}
                    disabled={!!merging}
                    className="h-6 text-[10px] gap-1 px-2"
                    data-testid={`merge-group-${gi}-btn`}
                  >
                    {merging === group.entries[0].id ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                    Keep Newest
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0 space-y-1.5">
                {group.entries.map((entry, ei) => (
                  <div
                    key={entry.id}
                    className={`flex items-start gap-2 p-2 rounded text-xs ${
                      ei === 0 ? 'bg-green-500/10 border border-green-500/20' : 'bg-muted/30 opacity-70'
                    }`}
                    data-testid={`dedup-entry-${gi}-${ei}`}
                  >
                    {ei === 0 && <CheckCircle2 className="w-3.5 h-3.5 text-green-500 shrink-0 mt-0.5" />}
                    {ei > 0 && <Trash2 className="w-3.5 h-3.5 text-muted-foreground shrink-0 mt-0.5" />}
                    <div className="min-w-0 flex-1">
                      <span className="text-muted-foreground">{formatTs(entry.timestamp)}</span>
                      {ei === 0 && <span className="ml-1 text-green-600 font-medium">(keep)</span>}
                      {ei > 0 && <span className="ml-1 text-muted-foreground">(remove)</span>}
                      <p className="text-foreground/80 mt-0.5 break-words">{truncate(entry.original_notes)}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </>
      )}
    </div>
  );
};

export default HistoryDedup;
